import { Button, Icon, Section, Skeleton, Text } from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../identity/session';
import { useTasks, type TaskFilter } from '../api';
import { TaskFormDialog, type TaskContext } from './TaskFormDialog';
import { TaskList } from './TaskList';
import styles from './tasks.module.css';

export interface TasksCardProps {
  filter: TaskFilter;
  context: TaskContext;
}

/** Open and recently done tasks of a record, with "add task" (TSK-002). */
export function TasksCard({ filter, context }: TasksCardProps) {
  const { t } = useTranslation();
  const { can } = useSession();
  const [creating, setCreating] = useState(false);
  const tasks = useTasks({ ...filter, view: 'all' }, can('tasks.read'));
  if (!can('tasks.read')) return null;
  const rows = tasks.data ?? [];
  return (
    <Section
      title={t('tasks.card.title')}
      action={
        can('tasks.write') && (
          <Button
            variant="plain"
            size="sm"
            aria-label={t('tasks.new')}
            iconStart={<Icon name="plus" size="sm" />}
            onClick={() => setCreating(true)}
          />
        )
      }
    >
      {tasks.isPending ? (
        <div className={styles.empty}>
          <Skeleton lines={2} />
        </div>
      ) : rows.length === 0 ? (
        <Text tone="muted" className={styles.empty}>
          {t('tasks.card.empty')}
        </Text>
      ) : (
        <TaskList tasks={rows} hideRefs />
      )}
      {creating && <TaskFormDialog open context={context} onClose={() => setCreating(false)} />}
    </Section>
  );
}
